// §11.4 / §9.1 — the Browser as an in-process app (D-016).
//
// It holds no capability: everything it does is either a frame on somebody else's origin or a call
// through `HangarAPI`. That is the point — an app that could reach further would be a proxy with
// extra steps, and D-015 says there is never one of those.
import { createRoot, type Root } from 'react-dom/client';
import type { InProcessApp } from '@hangar/shared';
import { COPY } from '@/copy';
import { Browser } from './Browser';
import './browser.css';

const roots = new WeakMap<HTMLElement, Root>();

export const browserApp: InProcessApp = {
  id: 'browser',
  name: 'Browser',
  icon: { glyph: 'globe', hue: 195 },
  keywords: ['web', 'internet', 'url', 'site', 'tabs'],
  window: { width: 980, height: 660, minWidth: 480, minHeight: 320, multiWindow: true },
  jumpList: [{ id: 'new-window', label: COPY.browser.newWindow }],
  // §10.2 / §34.3 — a URL is the one thing this app can be handed. Declared so a Share of a link
  // offers the Browser as a target, and so "open this in the Browser" is one mechanism rather than
  // a private channel for the markdown preview.
  accepts: [{ mime: ['text/uri-list'], action: 'open' }],

  onCommand(commandId, api) {
    if (commandId === 'new-window') void api.launch.app('browser');
  },

  mount(container, api) {
    const root = createRoot(container);
    roots.set(container, root);
    root.render(<Browser api={api} />);
  },

  unmount(container) {
    const root = roots.get(container);
    roots.delete(container);
    root?.unmount();
  },
};
